/**
 * 開発ポイント推移カード（日進ビュー）の「目標設定」モーダル。
 *
 * メンバー本人が、会社の期待値戦闘力とは別に自分の目標線を登録する。
 * 目標pt（1日あたり）・適用期間・線色を入力でき、保存すると日進ビューに
 * 指定色の破線として重なる。
 *
 * 期間の異なる目標線は何本でも持てる（例: 8月 4pt → 9月 6pt）。新しい目標を
 * 足しても期間が重ならない限り既存の目標線は消えない。期間が 1 日でも重なる
 * 登録は API が 409 で拒否し、このモーダルが「どの目標線と重なったか」を
 * 警告として出す（既存の目標線は変更されない）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 開発pt 自己設定目標線の設定モーダル。設定済み目標線の一覧・編集・解除と、新規追加フォーム（目標pt・適用期間・線色）を提供し、期間重複は警告で弾く
 * @graph-connects useSaveMemberDevelopPointTarget [calls] 自己設定目標線の追加・編集
 * @graph-connects useDeleteMemberDevelopPointTarget [calls] 自己設定目標線の解除
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '@cortex/web-design-system/components/ui/Modal';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Input } from '@cortex/web-design-system/components/ui/Input';
import {
  useSaveMemberDevelopPointTarget,
  useDeleteMemberDevelopPointTarget,
} from '../../hooks/useMember.js';
import { ApiError } from '../../lib/api.js';
import type {
  DevelopPointTarget,
  DevelopPointTargetConflict,
} from '../../types/develop-point-target.js';

/** 既定の目標線色（期待値戦闘力のインディゴ・現時点戦闘力のピンクと被らないアンバー） @graph-connects none */
export const DEFAULT_TARGET_COLOR = '#f59e0b';

/** 色ピッカーのプリセット（既存の系列色と区別しやすい 6 色） @graph-connects none */
const COLOR_PRESETS = ['#f59e0b', '#a855f7', '#06b6d4', '#ef4444', '#14b8a6', '#3b82f6'];

/** ローカル日付を YYYY-MM-DD にする（toISOString は UTC 化で日付がずれるため使わない） @graph-connects none */
function toDateInputValue(date: Date): string {
  const y = String(date.getFullYear());
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** YYYY-MM-DD をローカル Date に戻す（Date(string) は UTC 解釈になるため自前で組む） @graph-connects none */
function fromDateInputValue(iso: string): Date {
  return new Date(Number(iso.slice(0, 4)), Number(iso.slice(5, 7)) - 1, Number(iso.slice(8, 10)));
}

/**
 * 新規追加フォームの初期期間。
 * 既存の目標線があれば「最後の目標線の翌日 〜 その月末」を既定にして、
 * 開いた直後の入力がいきなり期間重複にならないようにする。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 新規目標線フォームの既定期間を、既存目標線と重ならない「最終期間の翌日〜月末」として算出する
 * @graph-connects none
 */
export function nextDefaultPeriod(targets: DevelopPointTarget[]): {
  startDate: string;
  endDate: string;
} {
  const latestEnd = targets.reduce<string | null>(
    (max, t) => (max === null || t.endDate > max ? t.endDate : max),
    null,
  );
  const start =
    latestEnd === null
      ? new Date(new Date().getFullYear(), new Date().getMonth(), 1)
      : (() => {
          const d = fromDateInputValue(latestEnd);
          d.setDate(d.getDate() + 1);
          return d;
        })();
  const end = new Date(start.getFullYear(), start.getMonth() + 1, 0);
  return { startDate: toDateInputValue(start), endDate: toDateInputValue(end) };
}

/** まだ使われていないプリセット色を選ぶ（全部使い切っていたら先頭に戻る） @graph-connects none */
export function nextUnusedColor(targets: DevelopPointTarget[]): string {
  const used = new Set(targets.map((t) => t.color.toLowerCase()));
  return COLOR_PRESETS.find((preset) => !used.has(preset)) ?? DEFAULT_TARGET_COLOR;
}

/** 一覧表示用の期間ラベル @graph-connects none */
function periodLabel(startDate: string, endDate: string): string {
  return `${startDate} 〜 ${endDate}`;
}

/** @graph-connects none */
interface DevelopPointsTargetModalProps {
  open: boolean;
  onClose: () => void;
  /** 対象メンバーの email（= ログイン中ユーザー。API 側でも本人判定する） */
  email: string;
  /** 設定済みの目標線一覧（開始日昇順） */
  targets: DevelopPointTarget[];
}

/**
 * 閉じている間はフォームをアンマウントしておくラッパー。
 * こうすると開くたびにフォーム state と mutation state（前回のエラー）が初期化され、
 * 同期用の useEffect を持たずに既定値から編集を始められる。
 * @graph-connects none
 */
export function DevelopPointsTargetModal({
  open,
  onClose,
  email,
  targets,
}: DevelopPointsTargetModalProps) {
  if (!open) return null;
  return <DevelopPointsTargetForm onClose={onClose} email={email} targets={targets} />;
}

/** @graph-connects none */
function DevelopPointsTargetForm({
  onClose,
  email,
  targets,
}: Omit<DevelopPointsTargetModalProps, 'open'>) {
  const { t } = useTranslation();
  const saveMutation = useSaveMemberDevelopPointTarget(email);
  const deleteMutation = useDeleteMemberDevelopPointTarget(email);

  const [initial] = useState(() => nextDefaultPeriod(targets));
  /** 編集中の目標線 id。null なら新規追加 */
  const [editingId, setEditingId] = useState<string | null>(null);
  const [targetPt, setTargetPt] = useState('');
  const [startDate, setStartDate] = useState(initial.startDate);
  const [endDate, setEndDate] = useState(initial.endDate);
  const [color, setColor] = useState(() => nextUnusedColor(targets));
  const [validationError, setValidationError] = useState<string | null>(null);

  const isPending = saveMutation.isPending || deleteMutation.isPending;

  /** フォームを「新規追加」の初期状態へ戻す */
  const resetForm = (nextTargets: DevelopPointTarget[]) => {
    const period = nextDefaultPeriod(nextTargets);
    setEditingId(null);
    setTargetPt('');
    setStartDate(period.startDate);
    setEndDate(period.endDate);
    setColor(nextUnusedColor(nextTargets));
    setValidationError(null);
    saveMutation.reset();
    deleteMutation.reset();
  };

  const startEditing = (target: DevelopPointTarget) => {
    setEditingId(target.id);
    setTargetPt(String(target.targetPt));
    setStartDate(target.startDate);
    setEndDate(target.endDate);
    setColor(target.color);
    setValidationError(null);
    saveMutation.reset();
    deleteMutation.reset();
  };

  const handleSave = () => {
    const parsed = Number(targetPt);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setValidationError(t('members.detail.developPointsTargetErrorValue'));
      return;
    }
    if (startDate === '' || endDate === '' || startDate > endDate) {
      setValidationError(t('members.detail.developPointsTargetErrorPeriod'));
      return;
    }
    setValidationError(null);
    saveMutation.mutate(
      {
        ...(editingId !== null ? { id: editingId } : {}),
        targetPt: parsed,
        startDate,
        endDate,
        color,
      },
      {
        // 保存後もモーダルは開いたままにして、続けて別期間の目標を足せるようにする
        onSuccess: (updated) => {
          resetForm(updated.targets);
        },
      },
    );
  };

  const handleDelete = (id: string) => {
    setValidationError(null);
    deleteMutation.mutate(id, {
      onSuccess: (updated) => {
        if (editingId === id) resetForm(updated.targets);
      },
    });
  };

  const mutationError = saveMutation.error ?? deleteMutation.error;
  const overlapConflicts =
    mutationError instanceof ApiError && mutationError.code === 'PERIOD_OVERLAP'
      ? ((mutationError.details?.conflicts ?? []) as DevelopPointTargetConflict[])
      : [];
  const errorMessage =
    validationError ??
    (mutationError
      ? mutationError instanceof Error
        ? mutationError.message
        : t('members.detail.developPointsTargetSaveFailed')
      : null);

  return (
    <Modal
      open
      onClose={isPending ? () => undefined : onClose}
      title={t('members.detail.developPointsTargetTitle')}
      description={t('members.detail.developPointsTargetDescription')}
      size="lg"
      // 設定済み目標線の一覧（期間 + pt + 操作ボタン）が 1 行に収まるよう lg より広く取る。
      // Modal は className を cn(twMerge) でマージするため max-w-lg を後勝ちで上書きできる。
      className="max-w-3xl"
      footer={
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="shrink-0">
            {editingId !== null && (
              <Button
                variant="ghost"
                onClick={() => {
                  resetForm(targets);
                }}
                disabled={isPending}
                data-testid="develop-points-target-cancel-edit"
              >
                {t('members.detail.developPointsTargetCancelEdit')}
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button variant="ghost" onClick={onClose} disabled={isPending}>
              {t('actions.close')}
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              loading={saveMutation.isPending}
              disabled={deleteMutation.isPending}
              data-testid="develop-points-target-save"
            >
              {editingId !== null
                ? t('actions.update')
                : t('members.detail.developPointsTargetAdd')}
            </Button>
          </div>
        </div>
      }
    >
      {errorMessage != null && (
        <div
          className="mb-4 p-3 rounded-lg bg-[var(--color-error)]/10 border border-[var(--color-error)]/30 text-sm text-[var(--color-error)]"
          role="alert"
          data-testid="develop-points-target-error"
        >
          <p>{errorMessage}</p>
          {overlapConflicts.length > 0 && (
            <ul className="mt-1 list-disc list-inside text-xs">
              {overlapConflicts.map((conflict) => (
                <li key={conflict.id}>{periodLabel(conflict.startDate, conflict.endDate)}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* 設定済みの目標線。ここで編集・解除しても他の目標線は消えない */}
      {targets.length > 0 && (
        <div className="mb-5">
          <p className="text-sm font-medium text-[var(--color-text-secondary)] mb-2">
            {t('members.detail.developPointsTargetExisting')}
          </p>
          <ul className="space-y-1.5" data-testid="develop-points-target-list">
            {targets.map((target) => (
              <li
                key={target.id}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg border text-sm ${
                  editingId === target.id
                    ? 'border-[var(--color-border-focus)] bg-[var(--color-bg-elevated)]'
                    : 'border-[var(--color-border)]'
                }`}
                data-testid={`develop-points-target-row-${target.id}`}
              >
                <span
                  className="inline-block w-5 border-t-2 border-dashed shrink-0"
                  style={{ borderColor: target.color }}
                  aria-hidden="true"
                />
                <span className="text-[var(--color-text-primary)] tabular-nums">
                  {periodLabel(target.startDate, target.endDate)}
                </span>
                <span className="font-semibold text-[var(--color-text-primary)] tabular-nums">
                  {target.targetPt}pt
                </span>
                <span className="ml-auto flex items-center gap-1 shrink-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      startEditing(target);
                    }}
                    disabled={isPending}
                    data-testid={`develop-points-target-edit-${target.id}`}
                  >
                    {t('actions.edit')}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      handleDelete(target.id);
                    }}
                    disabled={isPending}
                    data-testid={`develop-points-target-delete-${target.id}`}
                  >
                    {t('members.detail.developPointsTargetClear')}
                  </Button>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="space-y-4">
        <p className="text-sm font-medium text-[var(--color-text-secondary)]">
          {editingId !== null
            ? t('members.detail.developPointsTargetEditing')
            : t('members.detail.developPointsTargetNew')}
        </p>

        <Input
          type="number"
          label={t('members.detail.developPointsTargetValue')}
          helperText={t('members.detail.developPointsTargetValueHint')}
          min={0.1}
          step={0.1}
          value={targetPt}
          onChange={(e) => {
            setTargetPt(e.target.value);
          }}
          fullWidth
          data-testid="develop-points-target-value"
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            type="date"
            label={t('members.detail.developPointsTargetStartDate')}
            value={startDate}
            onChange={(e) => {
              setStartDate(e.target.value);
            }}
            fullWidth
            data-testid="develop-points-target-start"
          />
          <Input
            type="date"
            label={t('members.detail.developPointsTargetEndDate')}
            value={endDate}
            onChange={(e) => {
              setEndDate(e.target.value);
            }}
            fullWidth
            data-testid="develop-points-target-end"
          />
        </div>
        <p className="-mt-2 text-xs text-[var(--color-text-muted)]">
          {t('members.detail.developPointsTargetPeriodHint')}
        </p>

        <div>
          <span className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1.5">
            {t('members.detail.developPointsTargetColor')}
          </span>
          <div className="flex items-center gap-3 flex-wrap">
            <input
              type="color"
              value={color}
              onChange={(e) => {
                setColor(e.target.value);
              }}
              aria-label={t('members.detail.developPointsTargetColor')}
              className="h-9 w-12 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)] cursor-pointer p-1"
              data-testid="develop-points-target-color"
            />
            <div className="flex items-center gap-1.5">
              {COLOR_PRESETS.map((preset) => (
                <button
                  key={preset}
                  type="button"
                  onClick={() => {
                    setColor(preset);
                  }}
                  aria-label={preset}
                  aria-pressed={color.toLowerCase() === preset}
                  className={`h-6 w-6 rounded-full border transition-transform hover:scale-110 ${
                    color.toLowerCase() === preset
                      ? 'border-[var(--color-text-primary)] ring-2 ring-[var(--color-border-focus)]'
                      : 'border-[var(--color-border)]'
                  }`}
                  style={{ backgroundColor: preset }}
                />
              ))}
            </div>
            <span className="inline-flex items-center gap-2 text-xs text-[var(--color-text-muted)]">
              <span
                className="inline-block w-8 border-t-2 border-dashed"
                style={{ borderColor: color }}
              />
              {t('members.detail.developPointsTargetPreview')}
            </span>
          </div>
        </div>
      </div>
    </Modal>
  );
}
